/**
 * Downloading the desktop app from this server (the releases live in a
 * private GitHub repository; the API proxies them, see
 * apps/api/src/routes/desktopDownloads.ts), and guessing which build fits the
 * visitor's computer.
 */
import { useEffect, useState } from 'react';
import { DESKTOP_DOWNLOADS_PATH, type DesktopDownload, type DesktopLatestRelease } from '@vidmakr/shared/desktop';
import { api } from './api';

export type DetectedOS = 'mac' | 'win' | 'linux' | 'ios' | 'android' | 'chromeos' | 'unknown';
export type DetectedArch = 'arm64' | 'x64' | 'unknown';

export interface Detected {
  os: DetectedOS;
  arch: DetectedArch;
}

export interface DetectInput {
  userAgent: string;
  /** navigator.userAgentData.platform (Chromium). */
  uaPlatform?: string;
  /** getHighEntropyValues(['architecture']).architecture: 'arm' | 'x86'. */
  architecture?: string;
  bitness?: string;
  /** WebGL UNMASKED_RENDERER_WEBGL, e.g. "ANGLE (Apple, ANGLE Metal Renderer: Apple M2, …)". */
  webglRenderer?: string;
  maxTouchPoints?: number;
}

/** Pure detection from what the browser tells us. */
export function detectFrom(i: DetectInput): Detected {
  const ua = i.userAgent;
  const p = (i.uaPlatform ?? '').toLowerCase();
  let os: DetectedOS = 'unknown';
  if (p === 'macos') os = 'mac';
  else if (p === 'windows') os = 'win';
  else if (p === 'android') os = 'android';
  else if (p === 'chrome os' || p === 'chromeos') os = 'chromeos';
  else if (p === 'linux') os = 'linux';
  else if (/iPhone|iPad|iPod/.test(ua)) os = 'ios';
  else if (/Android/.test(ua)) os = 'android';
  else if (/CrOS/.test(ua)) os = 'chromeos';
  else if (/Macintosh|Mac OS X/.test(ua)) os = (i.maxTouchPoints ?? 0) > 1 ? 'ios' : 'mac'; // iPadOS asks for desktop sites
  else if (/Windows/.test(ua)) os = 'win';
  else if (/Linux|X11/.test(ua)) os = 'linux';

  let arch: DetectedArch = 'unknown';
  const a = (i.architecture ?? '').toLowerCase();
  if (a === 'arm') arch = 'arm64';
  else if (a === 'x86') arch = 'x64';
  else if (/aarch64|arm64|ARM64/.test(ua)) arch = 'arm64';
  else if (/x86_64|x64|Win64|WOW64|amd64/i.test(ua)) arch = 'x64';

  if (os === 'mac' && arch === 'unknown') {
    // Macs report "Intel Mac OS X" in every browser, so ask the GPU.
    const r = i.webglRenderer ?? '';
    if (/Apple M\d|Apple GPU/i.test(r)) arch = 'arm64';
    else if (/Intel|AMD|Radeon|NVIDIA|GeForce/i.test(r)) arch = 'x64';
  }
  return { os, arch };
}

function webglRenderer(): string {
  try {
    const gl = document.createElement('canvas').getContext('webgl');
    if (!gl) return '';
    const ext = gl.getExtension('WEBGL_debug_renderer_info');
    const r = String(gl.getParameter(ext ? ext.UNMASKED_RENDERER_WEBGL : gl.RENDERER) ?? '');
    gl.getExtension('WEBGL_lose_context')?.loseContext();
    return r;
  } catch {
    return '';
  }
}

interface UAData {
  platform?: string;
  getHighEntropyValues?: (hints: string[]) => Promise<{ architecture?: string; bitness?: string; platform?: string }>;
}

export async function detectPlatform(): Promise<Detected> {
  const uad = (navigator as Navigator & { userAgentData?: UAData }).userAgentData;
  const input: DetectInput = { userAgent: navigator.userAgent, uaPlatform: uad?.platform, maxTouchPoints: navigator.maxTouchPoints };
  if (uad?.getHighEntropyValues) {
    try {
      const h = await uad.getHighEntropyValues(['architecture', 'bitness']);
      input.architecture = h.architecture;
      input.bitness = h.bitness;
    } catch {
      // not allowed: fall back to the user agent
    }
  }
  const first = detectFrom(input);
  if (first.os === 'mac' && first.arch === 'unknown') input.webglRenderer = webglRenderer();
  return detectFrom(input);
}

export function useDetectedPlatform(): Detected | null {
  const [d, setD] = useState<Detected | null>(null);
  useEffect(() => {
    let live = true;
    void detectPlatform().then((x) => live && setD(x));
    return () => {
      live = false;
    };
  }, []);
  return d;
}

export type LatestState = { status: 'loading' } | { status: 'ok'; release: DesktopLatestRelease } | { status: 'error'; error: string };

let cached: Promise<DesktopLatestRelease> | null = null;

/** The newest desktop release, from this server. */
export function useLatestDesktopRelease(): LatestState {
  const [s, setS] = useState<LatestState>({ status: 'loading' });
  useEffect(() => {
    let live = true;
    cached ??= api<DesktopLatestRelease>(`${DESKTOP_DOWNLOADS_PATH}/latest`);
    cached.then(
      (release) => live && setS({ status: 'ok', release }),
      (e: Error) => {
        cached = null;
        if (live) setS({ status: 'error', error: e.message });
      },
    );
    return () => {
      live = false;
    };
  }, []);
  return s;
}

export const OS_NAMES: Record<'mac' | 'win' | 'linux', string> = { mac: 'macOS', win: 'Windows', linux: 'Linux' };

export const VARIANT_NAMES: Record<string, string> = {
  'mac/arm64': 'Apple Silicon',
  'mac/x64': 'Intel',
  'win/x64': 'x64',
  'win/arm64': 'ARM64',
  'linux/appimage': 'AppImage',
  'linux/deb': '.deb (Debian, Ubuntu)',
};

export function allDownloads(r: DesktopLatestRelease): DesktopDownload[] {
  return [r.downloads.mac.arm64, r.downloads.mac.x64, r.downloads.win.x64, r.downloads.win.arm64, r.downloads.linux.appimage, r.downloads.linux.deb].filter(
    (d): d is DesktopDownload => !!d,
  );
}

/** The build to offer first, or null on phones, tablets and unknown systems. */
export function recommended(r: DesktopLatestRelease, d: Detected | null): DesktopDownload | null {
  if (!d) return null;
  if (d.os === 'mac') return (d.arch === 'x64' ? r.downloads.mac.x64 : r.downloads.mac.arm64) ?? r.downloads.mac.arm64 ?? r.downloads.mac.x64 ?? null;
  if (d.os === 'win') return (d.arch === 'arm64' ? r.downloads.win.arm64 : r.downloads.win.x64) ?? r.downloads.win.x64 ?? null;
  if (d.os === 'linux') return r.downloads.linux.appimage ?? r.downloads.linux.deb ?? null;
  return null;
}

/** "Mac (Apple Silicon)", "Windows", "Windows (ARM)", "Linux": the short form for buttons in tight places. */
export function shortLabel(x: DesktopDownload): string {
  if (x.platform === 'mac') return `Mac (${x.variant === 'x64' ? 'Intel' : 'Apple Silicon'})`;
  if (x.platform === 'win') return x.variant === 'arm64' ? 'Windows (ARM)' : 'Windows';
  return 'Linux';
}

/** File type shown under a download button: ".dmg", ".exe", "AppImage", ".deb". */
export function formatOf(x: DesktopDownload): string {
  const m = /\.(dmg|exe|AppImage|deb|zip|msi)$/.exec(x.file);
  return m ? (m[1] === 'AppImage' ? 'AppImage' : `.${m[1]}`) : '';
}

/** The other builds for the same system (Intel next to Apple Silicon, .deb next to AppImage, …). */
export function alternatives(r: DesktopLatestRelease, x: DesktopDownload): DesktopDownload[] {
  return allDownloads(r).filter((d) => d.platform === x.platform && d.variant !== x.variant);
}

/** Short name of an alternative build next to the main one. */
export function alternativeLabel(x: DesktopDownload): string {
  if (x.platform === 'mac') return x.variant === 'x64' ? 'Intel' : 'Apple Silicon';
  if (x.platform === 'win') return x.variant === 'arm64' ? 'ARM' : 'x64';
  return x.variant === 'deb' ? '.deb' : 'AppImage';
}

export function isPhoneOrTablet(d: Detected | null): boolean {
  return !!d && (d.os === 'ios' || d.os === 'android');
}

export function downloadLabel(x: DesktopDownload): string {
  return `${OS_NAMES[x.platform]} (${VARIANT_NAMES[`${x.platform}/${x.variant}`] ?? x.variant})`;
}
